import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import EditElement from '../dashboard/components/EditElement';
import DeleteElement from '../dashboard/components/DeleteElement';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { notresultsfound } from '../../../assets/images/icons';
import { PencilSquareIcon, PlusIcon } from '@heroicons/react/24/solid';
import { useRefresh } from '../../../utils/RefreshProvider';
import PreviewSlider from './PreviewSlider';
import { truncateString } from '../../../utils/truncateString';

export default function CarouselsList() {
  const { t } = useTranslation();
  const localhost = process.env.REACT_APP_LOCALHOST;
  const { refreshKey } = useRefresh();
  const [carousels, setCarousels] = useState([]);
  const [search, setsearch] = useState("");
  const [loading, setloading] = useState(true);

  useEffect(() => {
    const fetchCarousels = async () => {
      try {
        const response = await axios.get(localhost + 'all-carousels');
        const data = response.data;
        const updatedData = data.map(item => ({ ...item, title: JSON.parse(item.title), details: JSON.parse(item.details) }));
        setCarousels(updatedData);
      } catch (error) {
        console.error("Error fetching carousels:", error);
      } finally {
        setloading(false);
      }
    };
    fetchCarousels();
  }, [refreshKey]);

  const ths = ["slide", "title", "description", "actions"];

  const handleSearch = (e) => {
    e.preventDefault();
    const filtered = carousels.filter((cat) =>
      cat.name[i18next.language].toLowerCase().includes(search.toLowerCase())
    );
    setCarousels(filtered);
  };

  // Mobile Card Component
  const MobileCard = ({ item }) => (
    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
      <div className="flex justify-between items-start mb-3">
        <div>
          <span className="text-xs text-gray-500 block mb-1">{t("category-id")}</span>
          <span className="font-semibold text-gray-900"># {item.id}</span>
        </div>
        <div className="flex gap-0.5">
          <PreviewSlider carouselId={item.id} localhost={localhost} carouselScreen='mobile' />
          <EditElement elementId={item.id} />
          <DeleteElement elementId={item.id} typeProp="category" />
        </div>
      </div>
      <dl className="space-y-3">
        <div>
          <dt className="text-xs text-gray-500 mb-1">{t("name")}</dt>
          <dd className="text-sm text-gray-900">{truncateString(item.title[i18next.language], 30)}</dd>
        </div>
        <div>
          <dt className="text-xs text-gray-500 mb-1">{t("description")}</dt>
          <dd className="text-sm text-gray-600">{truncateString(item.details[i18next.language], 30)}</dd>
        </div>
      </dl>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl font-bold text-gray-900">{t("all-slides")}</h1>
        <a href='/admin/create-slide' className="flex flex-wrap gap-1 items-center text-sm bg-black hover:bg-black/80 text-white pl-2 pr-3 py-2 max-sm:p-1.5 rounded-md">
          <PlusIcon className="h-[18px] w-[18px]" />
          <span className="max-sm:hidden">{t('add-slide')}</span>
        </a>
      </div>

      {/* Search Section */}
      <div className="bg-white rounded-lg shadow-sm p-4">
        <form onSubmit={handleSearch} className="flex gap-2 max-w-md">
          <input className="flex-1 border border-gray-300 rounded-lg px-4 py-1.5 text-sm outline-none transition-all" placeholder={t("search-slides")} type="text" value={search} onChange={(e) => setsearch(e.target.value)} />
          <button type="submit" className="px-3 py-1.5 bg-black hover:bg-black/80 text-white rounded-lg transition-colors duration-200 flex items-center gap-2">
            <MagnifyingGlassIcon className="h-5 w-5" />
            <span className="hidden sm:inline">{t("search")}</span>
          </button>
        </form>
      </div>

      {/* Content Section */}
      {loading ? (
        <div className="min-h-[55vh] flex items-center justify-center">
          <div className="loading-spinner" />
        </div>
      ) : carousels.length > 0 ? (
        <>
          {/* Desktop Table - Hidden on Mobile */}
          <div className="hidden md:block bg-white rounded-lg shadow-sm overflow-hidden border border-gray-200">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    {ths.map((item, index) => (
                      <th key={index} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{index === 0 ? t(item) + " ID" : t(item)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {carousels.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50 transition-colors duration-150">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900"># {item.num_slide}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{truncateString(item.title[i18next.language], 30)}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">{truncateString(item.details[i18next.language], 30)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        <div className="flex items-center gap-2">
                          <PreviewSlider carouselId={item.id} localhost={localhost} carouselScreen='mobile' />
                          <a href={'/admin/update-slide/' + item.id} className="text-black transition-colors duration-150 hover:bg-black/10 rounded-md p-1.5">
                            <PencilSquareIcon className="h-5 w-5" />
                          </a>
                          <DeleteElement elementId={item.id} typeProp="slide" />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile Cards - Shown only on Mobile */}
          <div className="md:hidden grid gap-4">
            {carousels.map((item, index) => (
              <MobileCard key={index} item={item} />
            ))}
          </div>
        </>
      ) : (
        <div className="flex flex-col items-center justify-center py-12 bg-white rounded-lg shadow-sm">
          <img loading="lazy" src={notresultsfound} alt="No results found" className="w-40 h-auto mb-4" />
        </div>
      )}
    </div>
  );
};
